import { Fragment, useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useApp } from "@/contexts/AppContext";
import { Button } from "@/components/ui/button";
import PageHeader from "@/components/PageHeader";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { FileText, Trophy, LayoutGrid, CalendarDays, FileDown, FileSpreadsheet } from "lucide-react";
import { toast } from "sonner";
import * as XLSX from "xlsx-js-style";
import { downloadCertificatePdf } from "@/lib/pdf";

// Helper: mix hex color with white → light background variant.
function lighten(hex, alpha = 0.15) {
  return `${hex}${Math.round(alpha * 255).toString(16).padStart(2, "0")}`;
}

// يختار لون خط أبيض أو داكن حسب سطوع لون الخلفية — يضمن وضوح الرقم/النص
// فوق أي لون تعبئة من نظام الألوان (بما فيها الألوان الفاتحة كـ"لم تُرصد").
function contrastTextColor(hex) {
  if (!hex) return "111827";
  const c = hex.replace("#", "");
  const r = parseInt(c.substring(0, 2), 16);
  const g = parseInt(c.substring(2, 4), 16);
  const b = parseInt(c.substring(4, 6), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.6 ? "111827" : "FFFFFF";
}

export default function Reports() {
  const { ctx, meta } = useApp();
  const grades = meta?.grades_by_stage?.[ctx.stage] || [];
  const [grade, setGrade] = useState("");
  const [certType, setCertType] = useState("semester1");
  const [matrix, setMatrix] = useState(null);
  const [rank, setRank] = useState(null);
  const [detailed, setDetailed] = useState(null);
  // إظهار/إخفاء الدرجات في "مراجعة الرصد" — يؤثر فقط على العمود الملوَّن
  // فعليًا حسب نظام الألوان (matrix.use_bands = الروضة/الابتدائي)؛ التلوين
  // نفسه يبقى ظاهرًا دائمًا، الخيار يتحكم فقط بظهور الرقم فوقه أو إخفائه.
  const [showGrades, setShowGrades] = useState(true);
  // مقاس صفحة "PDF 2" (نهاية العام الكامل) — قابل للاختيار A4/A3 (افتراضيًا
  // A3 لأنه تقرير عريض جدًا)، بلا أي تأثير على PDF الآخر (لسه A4 دائمًا).
  const [fullSheetPdfFormat, setFullSheetPdfFormat] = useState("a3");
  const [loading, setLoading] = useState(false);
  const [school, setSchool] = useState(null);

  useEffect(() => {
    api.get("/settings").then(({ data }) => setSchool(data)).catch(() => {});
  }, []);

  const load = async () => {
    if (!grade) return;
    setLoading(true);
    try {
      const semParam = certType === "end_year" ? "end_year"
                        : (certType === "semester1" ? "1" : "2");
      const [{ data: m }, { data: r }] = await Promise.all([
        api.get("/reports/review", { params: {
          stage: ctx.stage, year: ctx.year, grade, semester: semParam,
        } }),
        api.get("/reports/rank", { params: {
          stage: ctx.stage, year: ctx.year, grade, cert_type: certType,
        } }),
      ]);
      setMatrix(m); setRank(r);
      // End-year detailed only when relevant
      if (certType === "end_year") {
        const { data: d } = await api.get("/reports/end-year-detailed", {
          params: { stage: ctx.stage, year: ctx.year, grade },
        });
        setDetailed(d);
      } else {
        setDetailed(null);
      }
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  const exportExcel = () => {
    if (certType === "end_year") {
      if (!detailed) return;
      exportEndYearDetailedExcel();
      return;
    }
    if (!matrix) return;
    const showOfficial = matrix.show_official_grade;
    const showElementary = matrix.show_elementary_scale;
    const colsPerSubject = (showOfficial || showElementary) ? 4 : 3;

    const HEADER_TEAL = "0D9488";
    const GRID_COLOR = "94A3B8";
    const DIVIDER_COLOR = "1F2937";
    const STRIPE_COLOR = "F1F5F9";

    const thinGrid = { style: "thin", color: { rgb: GRID_COLOR } };
    const thickDivider = { style: "thick", color: { rgb: DIVIDER_COLOR } };
    const baseBorder = { top: thinGrid, bottom: thinGrid, left: thinGrid, right: thinGrid };
    const headerFont = { color: { rgb: "FFFFFF" }, bold: true, sz: 10 };
    const centerAlign = { horizontal: "center", vertical: "center", wrapText: true };

    // عدد الأعمدة الإجمالي (طالب + جلوس + كل مادة × 3 أو 4 أعمدة)
    let totalCols = 2;
    matrix.subjects.forEach(() => { totalCols += colsPerSubject; });

    // صفوف بيانات المدرسة أعلى الجدول (الوزارة، الإدارة، مكتب التعليم، اسم المدرسة)
    const schoolLines = [
      school?.ministry_name || "وزارة التعليم",
      ...(school?.education_dept ? [school.education_dept] : []),
      ...(school?.office_name ? [school.office_name] : []),
      school?.school_name || "المدرسة",
    ];
    const SCHOOL_ROWS = schoolLines.length;
    const TITLE_ROW = SCHOOL_ROWS;
    const META_ROW = SCHOOL_ROWS + 1;
    const HR1 = SCHOOL_ROWS + 3, HR2 = SCHOOL_ROWS + 4;

    const aoa = [];
    for (let i = 0; i < HR2; i++) aoa.push(new Array(totalCols).fill(""));
    schoolLines.forEach((line, i) => { aoa[i][0] = line; });
    aoa[TITLE_ROW][0] = "مراجعة رصد الدرجات";
    aoa[META_ROW][0] = `الصف: ${grade} — ${CERT_TYPE_LABELS[certType]}`;
    aoa[HR1 - 1][0] = "الطالب";
    aoa[HR1 - 1][1] = "رقم الجلوس";
    let c = 2;
    matrix.subjects.forEach((s) => {
      aoa[HR1 - 1][c] = s.name;
      const subCols = showOfficial
        ? ["أعمال السنة", "الاختبار النهائي", "المجموع", "الدرجة الرسمية"]
        : showElementary
        ? ["أعمال السنة", "الاختبار النهائي", "الدرجة", "التقدير"]
        : ["أعمال السنة", "الاختبار النهائي", "المجموع"];
      subCols.forEach((label, k) => { aoa[HR2 - 1][c + k] = label; });
      c += colsPerSubject;
    });
    matrix.students.forEach((st) => {
      const row = new Array(totalCols).fill("");
      row[0] = st.name; row[1] = st.seat_number || "";
      let cc = 2;
      matrix.subjects.forEach((s) => {
        const cell = st.cells[s.name] || {};
        const vals = showOfficial
          ? [cell.year_work, cell.final_exam, cell.total, cell.official_grade]
          : showElementary
          ? [cell.year_work, cell.final_exam, cell.total, cell.label]
          : [cell.year_work, cell.final_exam, cell.total];
        vals.forEach((v, k) => {
          // العمود الملوَّن (المجموع/الدرجة الرسمية) فقط يخضع لخيار "إظهار
          // الدرجات" — عمودا أعمال السنة/الاختبار النهائي دائمًا ظاهران.
          // الابتدائي: matrix.use_bands أصبحت خاصة بالروضة فقط، فالقيم هنا
          // تظهر دائمًا تلقائيًا (لا معنى للإخفاء بلا ألوان).
          const isColoredCol = showOfficial ? k >= 2 : (showElementary ? k === 2 : k === 2);
          const hideThis = matrix.use_bands && isColoredCol && !showGrades;
          row[cc + k] = hideThis ? "" : (v ?? "-");
        });
        cc += colsPerSubject;
      });
      aoa.push(row);
    });

    const ws = XLSX.utils.aoa_to_sheet(aoa);
    ws["!rtl"] = true;

    // دمج: أسطر بيانات المدرسة والعنوان وسطر البيانات الوصفية، ورأس
    // اسم الطالب/رقم الجلوس عبر الصفّين، ورأس كل مادة أفقيًا
    const merges = [];
    for (let i = 0; i < SCHOOL_ROWS; i++) merges.push({ s: { r: i, c: 0 }, e: { r: i, c: totalCols - 1 } });
    merges.push({ s: { r: TITLE_ROW, c: 0 }, e: { r: TITLE_ROW, c: totalCols - 1 } });
    merges.push({ s: { r: META_ROW, c: 0 }, e: { r: META_ROW, c: totalCols - 1 } });
    merges.push({ s: { r: HR1 - 1, c: 0 }, e: { r: HR2 - 1, c: 0 } });
    merges.push({ s: { r: HR1 - 1, c: 1 }, e: { r: HR2 - 1, c: 1 } });
    let mc = 2;
    matrix.subjects.forEach(() => {
      merges.push({ s: { r: HR1 - 1, c: mc }, e: { r: HR1 - 1, c: mc + colsPerSubject - 1 } });
      mc += colsPerSubject;
    });
    ws["!merges"] = merges;

    const setCell = (r, cIdx, style) => {
      const addr = XLSX.utils.encode_cell({ r, c: cIdx });
      if (!ws[addr]) ws[addr] = { t: "s", v: "" };
      ws[addr].s = { ...(ws[addr].s || {}), ...style };
    };

    // تنسيق أسطر بيانات المدرسة والعنوان
    schoolLines.forEach((_, i) => {
      setCell(i, 0, { font: { bold: true, sz: i === SCHOOL_ROWS - 1 ? 13 : 11, color: { rgb: HEADER_TEAL } }, alignment: { horizontal: "center" } });
    });
    setCell(TITLE_ROW, 0, { font: { bold: true, sz: 13 }, alignment: { horizontal: "center" } });
    setCell(META_ROW, 0, { font: { italic: true, sz: 11 }, alignment: { horizontal: "center" } });

    // تنسيق رأس اسم الطالب/رقم الجلوس: خلفية تركوازية صلبة + نص أبيض
    for (const colIdx of [0, 1]) {
      setCell(HR1 - 1, colIdx, {
        fill: { patternType: "solid", fgColor: { rgb: HEADER_TEAL } },
        font: headerFont, alignment: centerAlign, border: baseBorder,
      });
    }

    // تنسيق رأس كل مادة (وأعمدتها الفرعية) بلون المادة الصلب الكامل + نص
    // أبيض، مع خط فاصل عريض بعد آخر عمود لكل مادة (باستثناء آخر مادة)
    let hc = 2;
    matrix.subjects.forEach((s, si) => {
      const isLastSubject = si === matrix.subjects.length - 1;
      const subjColorHex = s.color.replace("#", "");
      for (let k = 0; k < colsPerSubject; k++) {
        const isLastColOfSubject = k === colsPerSubject - 1;
        const border = { ...baseBorder };
        if (isLastColOfSubject && !isLastSubject) border.right = thickDivider;
        setCell(HR1 - 1, hc + k, {
          fill: { patternType: "solid", fgColor: { rgb: subjColorHex } },
          font: headerFont, alignment: centerAlign, border,
        });
        setCell(HR2 - 1, hc + k, {
          fill: { patternType: "solid", fgColor: { rgb: subjColorHex } },
          font: { ...headerFont, sz: 9 }, alignment: centerAlign, border,
        });
      }
      hc += colsPerSubject;
    });

    // تنسيق صفوف بيانات الطلاب: تبادل الصفوف + خلفية لونية صلبة (نجاح/رسوب)
    // لعمودي المجموع/الدرجة الرسمية فقط + نص أبيض، وخط فاصل عريض بين المواد
    matrix.students.forEach((st, ri) => {
      const rowIdx = HR2 + ri;
      const stripe = ri % 2 === 1 ? STRIPE_COLOR : "FFFFFF";
      setCell(rowIdx, 0, { fill: { patternType: "solid", fgColor: { rgb: stripe } }, border: baseBorder, alignment: { horizontal: "right", vertical: "center" } });
      setCell(rowIdx, 1, { fill: { patternType: "solid", fgColor: { rgb: stripe } }, border: baseBorder, alignment: centerAlign });
      let dc = 2;
      matrix.subjects.forEach((s, si) => {
        const isLastSubject = si === matrix.subjects.length - 1;
        const cell = st.cells[s.name] || {};
        const cellColor = cell.color ? cell.color.replace("#", "") : null;
        for (let k = 0; k < colsPerSubject; k++) {
          const isLastColOfSubject = k === colsPerSubject - 1;
          const border = { ...baseBorder };
          if (isLastColOfSubject && !isLastSubject) border.right = thickDivider;
          // اللون الصلب (نجاح/رسوب) يُطبَّق فقط على عمودي "المجموع" و"الدرجة
          // الرسمية" (آخر عمودين) للإعدادي، وليس على أعمال السنة/الاختبار
          // النهائي. الابتدائي: بلا أي تلوين إطلاقًا (نظام الألوان أُلغي
          // منها بالكامل) — حتى عمود "الدرجة" نفسه بلا لون، وعمود "التقدير"
          // نص عادي فقط.
          const isColoredCol = showElementary ? false : (showOfficial ? k >= 2 : k === 2);
          const fillColor = (isColoredCol && cellColor) ? cellColor : stripe;
          setCell(rowIdx, dc + k, {
            fill: { patternType: "solid", fgColor: { rgb: fillColor } },
            font: (isColoredCol && cellColor) ? { color: { rgb: contrastTextColor(cellColor) }, bold: true, sz: 10 } : { sz: 10, bold: showElementary && k >= 2 },
            alignment: centerAlign, border,
          });
        }
        dc += colsPerSubject;
      });
    });

    ws["!cols"] = [{ wch: 24 }, { wch: 10 }, ...matrix.subjects.flatMap(() =>
      Array(colsPerSubject).fill({ wch: 9 }))];
    ws["!rows"] = [...Array(SCHOOL_ROWS).fill({ hpt: 18 }), { hpt: 22 }, { hpt: 18 }, { hpt: 24 }, { hpt: 20 }];
    // إصلاح: الخاصية السابقة { xSplit: 2, ySplit: HR2 } كانت بصيغة غير
    // مطابقة للمواصفة الموثَّقة لـ SheetJS (تتطلب xSplit/ySplit كنصوص، مع
    // topLeftCell وactivePane وstate صراحة) — بدونها يُتجاهَل التجميد
    // بصمت أو يُكتَب بشكل غير صالح، وهذا سبب عدم عمله فعليًا رغم وجود
    // السطر. الشكل الصحيح موثَّق هنا: يجمّد أول عمودين (الطالب + رقم
    // الجلوس) ثابتَين أفقيًا بصرف النظر عن اتجاه RTL (التجميد يعتمد على
    // فهرس العمود في البيانات، لا على اتجاه العرض).
    ws["!freeze"] = {
      xSplit: "2", ySplit: String(HR2),
      topLeftCell: XLSX.utils.encode_cell({ r: HR2, c: 2 }),
      activePane: "bottomRight", state: "frozen",
    };

    const wb = XLSX.utils.book_new();
    wb.Workbook = { Views: [{ RTL: true }] };
    XLSX.utils.book_append_sheet(wb, ws, "مراجعة الرصد");
    XLSX.writeFile(wb, `مراجعة-رصد-${grade}-${certType}.xlsx`);
    toast.success("تم تصدير ملف Excel بنجاح");
  };

  // تصدير Excel خاص بنهاية العام فقط — 9 أعمدة لكل مادة (بنفس البنية
  // المعروضة بالفعل وبنجاح في تبويب "نهاية العام التفصيلي" على الشاشة):
  // لكل مادة → مجموعة "الفصل الأول" (أعمال السنة، النهائي، الخام، الرسمية)
  // + مجموعة "الفصل الثاني" (نفس الأربعة) + عمود "نهاية العام" النهائي.
  // الفصل الأول والثاني (matrix العادي) لم يُمسَّا إطلاقًا ولا يزالان
  // يصدَّران بنفس الطريقة القديمة الصحيحة عبر exportExcel أعلاه.
  const exportEndYearDetailedExcel = () => {
    const HEADER_TEAL = "0D9488";
    const GRID_COLOR = "94A3B8";
    const DIVIDER_COLOR = "1F2937";
    const STRIPE_COLOR = "F1F5F9";
    const SEM_GROUP_TEAL = "0F766E";

    const thinGrid = { style: "thin", color: { rgb: GRID_COLOR } };
    const thickDivider = { style: "thick", color: { rgb: DIVIDER_COLOR } };
    const baseBorder = { top: thinGrid, bottom: thinGrid, left: thinGrid, right: thinGrid };
    const headerFont = { color: { rgb: "FFFFFF" }, bold: true, sz: 10 };
    const centerAlign = { horizontal: "center", vertical: "center", wrapText: true };

    const COLS_PER_SUBJECT = 9; // yw1, fe1, raw1, official1, yw2, fe2, raw2, official2, end_year
    const subCols = ["أعمال السنة", "الاختبار النهائي", "الخام", "الرسمية"];
    const subKeys1 = ["yw1", "fe1", "raw1", "official1"];
    const subKeys2 = ["yw2", "fe2", "raw2", "official2"];

    let totalCols = 2;
    detailed.subjects.forEach(() => { totalCols += COLS_PER_SUBJECT; });

    const schoolLines = [
      school?.ministry_name || "وزارة التعليم",
      ...(school?.education_dept ? [school.education_dept] : []),
      ...(school?.office_name ? [school.office_name] : []),
      school?.school_name || "المدرسة",
    ];
    const SCHOOL_ROWS = schoolLines.length;
    const TITLE_ROW = SCHOOL_ROWS;
    const META_ROW = SCHOOL_ROWS + 1;
    // 3 صفوف رأس بدل 2: اسم المادة، ثم الفصل الأول/الثاني/نهاية العام، ثم أسماء الأعمدة الفرعية
    const HR1 = SCHOOL_ROWS + 3, HR2 = SCHOOL_ROWS + 4, HR3 = SCHOOL_ROWS + 5;

    const aoa = [];
    for (let i = 0; i < HR3; i++) aoa.push(new Array(totalCols).fill(""));
    schoolLines.forEach((line, i) => { aoa[i][0] = line; });
    aoa[TITLE_ROW][0] = "مراجعة رصد الدرجات — نهاية العام (تفصيلي)";
    aoa[META_ROW][0] = `الصف: ${grade} — نهاية العام`;
    aoa[HR1 - 1][0] = "الطالب";
    aoa[HR1 - 1][1] = "رقم الجلوس";

    let c = 2;
    detailed.subjects.forEach((s) => {
      aoa[HR1 - 1][c] = s.name;
      aoa[HR2 - 1][c] = "الفصل الأول";
      aoa[HR2 - 1][c + 4] = "الفصل الثاني";
      aoa[HR2 - 1][c + 8] = "نهاية العام";
      subCols.forEach((label, k) => {
        aoa[HR3 - 1][c + k] = label;
        aoa[HR3 - 1][c + 4 + k] = label;
      });
      aoa[HR3 - 1][c + 8] = "الدرجة";
      c += COLS_PER_SUBJECT;
    });

    detailed.students.forEach((st) => {
      const row = new Array(totalCols).fill("");
      row[0] = st.name; row[1] = st.seat_number || "";
      let cc = 2;
      detailed.subjects.forEach((s) => {
        const cell = st.subjects[s.name] || {};
        if (cell.type === "outcome") {
          row[cc] = cell.outcome_text || "-";
        } else {
          [...subKeys1, ...subKeys2, "end_year"].forEach((k, i) => {
            // نظام الألوان (الروضة/الابتدائي فقط): "الخام" و"الرسمية" لكل
            // فصل، ونهاية العام — تخضع لخيار "إظهار الدرجات" (القيمة تُخفى،
            // لكن التلوين نفسه يبقى ظاهرًا دائمًا عبر التنسيق أدناه).
            const isColoredKey = ["raw1", "official1", "raw2", "official2", "end_year"].includes(k);
            const hideThis = detailed.use_bands && isColoredKey && !showGrades;
            row[cc + i] = hideThis ? "" : (cell[k] ?? "-");
          });
        }
        cc += COLS_PER_SUBJECT;
      });
      aoa.push(row);
    });

    const ws = XLSX.utils.aoa_to_sheet(aoa);
    ws["!rtl"] = true;

    const merges = [];
    for (let i = 0; i < SCHOOL_ROWS; i++) merges.push({ s: { r: i, c: 0 }, e: { r: i, c: totalCols - 1 } });
    merges.push({ s: { r: TITLE_ROW, c: 0 }, e: { r: TITLE_ROW, c: totalCols - 1 } });
    merges.push({ s: { r: META_ROW, c: 0 }, e: { r: META_ROW, c: totalCols - 1 } });
    merges.push({ s: { r: HR1 - 1, c: 0 }, e: { r: HR3 - 1, c: 0 } });
    merges.push({ s: { r: HR1 - 1, c: 1 }, e: { r: HR3 - 1, c: 1 } });
    let mc = 2;
    detailed.subjects.forEach(() => {
      merges.push({ s: { r: HR1 - 1, c: mc }, e: { r: HR1 - 1, c: mc + COLS_PER_SUBJECT - 1 } });
      merges.push({ s: { r: HR2 - 1, c: mc }, e: { r: HR2 - 1, c: mc + 3 } });
      merges.push({ s: { r: HR2 - 1, c: mc + 4 }, e: { r: HR2 - 1, c: mc + 7 } });
      merges.push({ s: { r: HR2 - 1, c: mc + 8 }, e: { r: HR3 - 1, c: mc + 8 } });
      mc += COLS_PER_SUBJECT;
    });
    ws["!merges"] = merges;

    const setCell = (r, cIdx, style) => {
      const addr = XLSX.utils.encode_cell({ r, c: cIdx });
      if (!ws[addr]) ws[addr] = { t: "s", v: "" };
      ws[addr].s = { ...(ws[addr].s || {}), ...style };
    };

    schoolLines.forEach((_, i) => {
      setCell(i, 0, { font: { bold: true, sz: i === SCHOOL_ROWS - 1 ? 13 : 11, color: { rgb: HEADER_TEAL } }, alignment: { horizontal: "center" } });
    });
    setCell(TITLE_ROW, 0, { font: { bold: true, sz: 13 }, alignment: { horizontal: "center" } });
    setCell(META_ROW, 0, { font: { italic: true, sz: 11 }, alignment: { horizontal: "center" } });

    for (const colIdx of [0, 1]) {
      setCell(HR1 - 1, colIdx, {
        fill: { patternType: "solid", fgColor: { rgb: HEADER_TEAL } },
        font: headerFont, alignment: centerAlign, border: baseBorder,
      });
    }

    let hc = 2;
    detailed.subjects.forEach((s, si) => {
      const isLastSubject = si === detailed.subjects.length - 1;
      const subjColorHex = s.color.replace("#", "");
      for (let k = 0; k < COLS_PER_SUBJECT; k++) {
        const isLastCol = k === COLS_PER_SUBJECT - 1;
        const border = { ...baseBorder };
        if (isLastCol && !isLastSubject) border.right = thickDivider;
        setCell(HR1 - 1, hc + k, {
          fill: { patternType: "solid", fgColor: { rgb: subjColorHex } },
          font: headerFont, alignment: centerAlign, border,
        });
        // مجموعة الفصل (الأول/الثاني/نهاية العام) بظل أغمق قليلًا للتمييز البصري
        setCell(HR2 - 1, hc + k, {
          fill: { patternType: "solid", fgColor: { rgb: SEM_GROUP_TEAL } },
          font: { ...headerFont, sz: 9 }, alignment: centerAlign, border,
        });
        setCell(HR3 - 1, hc + k, {
          fill: { patternType: "solid", fgColor: { rgb: subjColorHex } },
          font: { ...headerFont, sz: 8 }, alignment: centerAlign, border,
        });
      }
      hc += COLS_PER_SUBJECT;
    });

    detailed.students.forEach((st, ri) => {
      const rowIdx = HR3 + ri;
      const stripe = ri % 2 === 1 ? STRIPE_COLOR : "FFFFFF";
      setCell(rowIdx, 0, { fill: { patternType: "solid", fgColor: { rgb: stripe } }, border: baseBorder, alignment: { horizontal: "right", vertical: "center" } });
      setCell(rowIdx, 1, { fill: { patternType: "solid", fgColor: { rgb: stripe } }, border: baseBorder, alignment: centerAlign });
      let dc = 2;
      detailed.subjects.forEach((s, si) => {
        const isLastSubject = si === detailed.subjects.length - 1;
        const cell = st.subjects[s.name] || {};
        const cellKeys = [...subKeys1, ...subKeys2, "end_year"];
        for (let k = 0; k < COLS_PER_SUBJECT; k++) {
          const isLastCol = k === COLS_PER_SUBJECT - 1;
          const border = { ...baseBorder };
          if (isLastCol && !isLastSubject) border.right = thickDivider;
          // نظام الألوان الفعلي (يفوق التوقعات/يلبي التوقعات/...) — فقط
          // لمراحل الروضة/الابتدائي، ولأعمدة "الخام"/"الرسمية"/"نهاية
          // العام" تحديدًا (بدل لون هوية المادة الذي كان يُستخدَم سابقًا
          // فقط لعمود نهاية العام بلا علاقة بالتقدير الفعلي).
          const colorKey = `${cellKeys[k]}_color`;
          const bandColor = detailed.use_bands ? cell[colorKey] : null;
          const fillHex = bandColor ? bandColor.replace("#", "") : stripe;
          setCell(rowIdx, dc + k, {
            fill: { patternType: "solid", fgColor: { rgb: fillHex } },
            font: bandColor ? { bold: true, sz: 10, color: { rgb: contrastTextColor(bandColor) } } : { sz: 10 },
            alignment: centerAlign, border,
          });
        }
        dc += COLS_PER_SUBJECT;
      });
    });

    ws["!cols"] = [{ wch: 24 }, { wch: 10 }, ...detailed.subjects.flatMap(() =>
      Array(COLS_PER_SUBJECT).fill({ wch: 8 }))];
    ws["!rows"] = [...Array(SCHOOL_ROWS).fill({ hpt: 18 }), { hpt: 22 }, { hpt: 18 }, { hpt: 20 }, { hpt: 18 }, { hpt: 20 }];
    ws["!freeze"] = {
      xSplit: "2", ySplit: String(HR3),
      topLeftCell: XLSX.utils.encode_cell({ r: HR3, c: 2 }),
      activePane: "bottomRight", state: "frozen",
    };

    const wb = XLSX.utils.book_new();
    wb.Workbook = { Views: [{ RTL: true }] };
    XLSX.utils.book_append_sheet(wb, ws, "نهاية العام - تفصيلي");
    XLSX.writeFile(wb, `مراجعة-رصد-${grade}-نهاية-العام-تفصيلي.xlsx`);
    toast.success("تم تصدير ملف Excel بنجاح");
  };

  // ══════════════════════ Excel 2 — الكشف الكامل لنهاية العام ══════════════════════
  // تقرير منفصل بالكامل عن الكشوف أعلاه (لا يستبدلها) — نمط "دفتر رصد
  // كامل": لكل طالب 3 صفوف (الفصل الأول / الفصل الثاني / الدور الأول)،
  // وعمود مستقل لكل مادة، مع رقم الجلوس/الاسم/الرقم القومي/السفارة/
  // المدرسة/نظام الدراسة/اللغات الأجنبية/الملاحظات. كل الأرقام والحقول من
  // /reports/end-year-detailed مباشرة — بلا أي إعادة حساب هنا.
  const exportEndYearFullSheetExcel = () => {
    if (!detailed) return;
    const HEADER_TEAL = "0D9488";
    const BORDER_GRAY = "94A3B8";
    const STRIPE_COLOR = "F1F5F9";
    const thinGrid = { style: "thin", color: { rgb: BORDER_GRAY } };
    const baseBorder = { top: thinGrid, bottom: thinGrid, left: thinGrid, right: thinGrid };
    const headerFont = { color: { rgb: "FFFFFF" }, bold: true, sz: 10 };
    const centerAlign = { horizontal: "center", vertical: "center", wrapText: true };

    const TERM_LABELS = ["الفصل الأول", "الفصل الثاني", "الدور الأول"];
    const ROWS_PER_STUDENT = 3;
    // ترتيب الأعمدة الثابتة كما طُلِب بالتحديد: رقم الجلوس لحاله، عمود
    // للاسم والرقم القومي، ثم السفارة والمدرسة، ثم "الفصل" بعدها مباشرة
    // وقبل أعمدة المواد.
    const FIXED_COLS = ["رقم الجلوس", "الاسم والرقم القومي", "السفارة والمدرسة", "الفصل"];
    const TAIL_COLS = ["المجموع الكلي", "نظام الدراسة", "اللغة الأجنبية الأولى", "اللغة الأجنبية الثانية", "ملاحظات"];
    const subjectNames = detailed.subjects.map((s) => s.name);
    const totalCols = FIXED_COLS.length + subjectNames.length + TAIL_COLS.length;

    const schoolLines = [
      school?.ministry_name || "وزارة التعليم",
      ...(school?.education_dept ? [school.education_dept] : []),
      ...(school?.office_name ? [school.office_name] : []),
      school?.school_name || "المدرسة",
    ];
    const SCHOOL_ROWS = schoolLines.length;
    const TITLE_ROW = SCHOOL_ROWS;
    const META_ROW = SCHOOL_ROWS + 1;
    const HR = SCHOOL_ROWS + 3;

    const aoa = [];
    for (let i = 0; i < HR; i++) aoa.push(new Array(totalCols).fill(""));
    schoolLines.forEach((line, i) => { aoa[i][0] = line; });
    aoa[TITLE_ROW][0] = "كشف رصد درجات — نهاية العام (كامل)";
    aoa[META_ROW][0] = `الصف: ${grade} — نهاية العام`;

    let c = 0;
    FIXED_COLS.forEach((label) => { aoa[HR - 1][c] = label; c += 1; });
    subjectNames.forEach((name, i) => {
      const s = detailed.subjects[i];
      aoa[HR - 1][c] = `${name} (${s.max_score}/${s.min_score})`;
      c += 1;
    });
    aoa[HR - 1][c] = `المجموع الكلي (${detailed.students[0]?.totals?.max ?? ""})`;
    TAIL_COLS.slice(1).forEach((label) => { c += 1; aoa[HR - 1][c] = label; });

    detailed.students.forEach((st) => {
      for (let t = 0; t < ROWS_PER_STUDENT; t++) {
        const row = new Array(totalCols).fill("");
        row[0] = t === 0 ? String(st.seat_number || "—") : "";
        row[1] = t === 0 ? `${st.name}\nرقم قومي: ${st.national_id || "—"}` : "";
        row[2] = t === 0 ? `${school?.embassy_name || "—"}\n${school?.school_name || "—"}` : "";
        row[3] = TERM_LABELS[t];
        let cc = 4;
        subjectNames.forEach((name) => {
          const cell = st.subjects[name] || {};
          if (cell.type === "outcome") {
            row[cc] = t === 2 ? (cell.outcome_text || "—") : "";
          } else {
            const val = t === 0 ? cell.official1 : t === 1 ? cell.official2 : cell.end_year;
            const colorKey = t === 0 ? "official1_color" : t === 1 ? "official2_color" : "end_year_color";
            const hideThis = detailed.use_bands && cell[colorKey] && !showGrades;
            row[cc] = hideThis ? "" : (val == null ? "—" : Number(val).toFixed(1));
          }
          cc += 1;
        });
        row[cc] = t === 0 ? st.totals.sem1 : t === 1 ? st.totals.sem2 : st.totals.end_year;
        cc += 1;
        row[cc] = t === 0 ? (st.section || "—") : ""; cc += 1;
        row[cc] = t === 0 ? (st.foreign_lang1 || "—") : ""; cc += 1;
        row[cc] = t === 0 ? (st.foreign_lang2 || "—") : ""; cc += 1;
        row[cc] = t === 0 ? (st.notes.join(" / ") || "") : "";
        aoa.push(row);
      }
    });

    const ws = XLSX.utils.aoa_to_sheet(aoa);
    ws["!rtl"] = true;

    const merges = [];
    for (let i = 0; i < SCHOOL_ROWS; i++) merges.push({ s: { r: i, c: 0 }, e: { r: i, c: totalCols - 1 } });
    merges.push({ s: { r: TITLE_ROW, c: 0 }, e: { r: TITLE_ROW, c: totalCols - 1 } });
    merges.push({ s: { r: META_ROW, c: 0 }, e: { r: META_ROW, c: totalCols - 1 } });

    // دمج رأسي لكل طالب: رقم الجلوس، الاسم/القومي، السفارة/المدرسة (3
    // أعمدة مستقلة الآن)، وكل أعمدة "الذيل" — تمتد الثلاث صفوف. "الفصل"
    // وحده بلا دمج (قيمته تتغيّر كل صف).
    detailed.students.forEach((_, si) => {
      const r0 = HR + si * ROWS_PER_STUDENT;
      const r1 = r0 + ROWS_PER_STUDENT - 1;
      merges.push({ s: { r: r0, c: 0 }, e: { r: r1, c: 0 } });
      merges.push({ s: { r: r0, c: 1 }, e: { r: r1, c: 1 } });
      merges.push({ s: { r: r0, c: 2 }, e: { r: r1, c: 2 } });
      const tailStart = FIXED_COLS.length + subjectNames.length + 1; // بعد المجموع الكلي
      for (let c2 = tailStart; c2 < totalCols; c2++) merges.push({ s: { r: r0, c: c2 }, e: { r: r1, c: c2 } });
    });
    ws["!merges"] = merges;

    const setCell = (r, cIdx, style) => {
      const addr = XLSX.utils.encode_cell({ r, c: cIdx });
      if (!ws[addr]) ws[addr] = { t: "s", v: "" };
      ws[addr].s = { ...(ws[addr].s || {}), ...style };
    };

    schoolLines.forEach((_, i) => {
      setCell(i, 0, { font: { bold: true, sz: i === SCHOOL_ROWS - 1 ? 13 : 11, color: { rgb: HEADER_TEAL } }, alignment: { horizontal: "center" } });
    });
    setCell(TITLE_ROW, 0, { font: { bold: true, sz: 13 }, alignment: { horizontal: "center" } });
    setCell(META_ROW, 0, { font: { italic: true, sz: 11 }, alignment: { horizontal: "center" } });
    for (let cc = 0; cc < totalCols; cc++) {
      setCell(HR - 1, cc, { fill: { patternType: "solid", fgColor: { rgb: HEADER_TEAL } }, font: headerFont, alignment: centerAlign, border: baseBorder });
    }
    detailed.students.forEach((st, si) => {
      const stripe = si % 2 === 1 ? STRIPE_COLOR : "FFFFFF";
      for (let t = 0; t < ROWS_PER_STUDENT; t++) {
        const r = HR + si * ROWS_PER_STUDENT + t;
        const colorKey = t === 0 ? "official1_color" : t === 1 ? "official2_color" : "end_year_color";
        for (let cc = 0; cc < totalCols; cc++) {
          const subjIdx = cc - 4;
          const isSubjectCol = subjIdx >= 0 && subjIdx < subjectNames.length;
          const cell = isSubjectCol ? (st.subjects[subjectNames[subjIdx]] || {}) : null;
          const bandColor = (isSubjectCol && detailed.use_bands) ? cell[colorKey] : null;
          setCell(r, cc, {
            fill: { patternType: "solid", fgColor: { rgb: bandColor ? bandColor.replace("#", "") : stripe } },
            border: baseBorder, alignment: centerAlign,
            // خط أسود صريح كافتراضي (وليس معتمِدًا على لون الخط الافتراضي)؛
            // فوق لون تقدير، يُختار لون متباين تلقائيًا بدل الأسود دائمًا.
            font: { sz: 9, bold: !!bandColor, color: { rgb: bandColor ? contrastTextColor(bandColor) : "111827" } },
          });
        }
      }
    });

    ws["!cols"] = [{ wch: 10 }, { wch: 22 }, { wch: 18 }, { wch: 9 }, ...subjectNames.map(() => ({ wch: 10 })),
      { wch: 10 }, { wch: 10 }, { wch: 13 }, { wch: 13 }, { wch: 26 }];
    ws["!freeze"] = {
      xSplit: "4", ySplit: String(HR),
      topLeftCell: XLSX.utils.encode_cell({ r: HR, c: 4 }),
      activePane: "bottomRight", state: "frozen",
    };

    const wb = XLSX.utils.book_new();
    wb.Workbook = { Views: [{ RTL: true }] };
    XLSX.utils.book_append_sheet(wb, ws, "نهاية العام - كشف كامل");
    XLSX.writeFile(wb, `كشف-كامل-نهاية-العام-${grade}.xlsx`);
    toast.success("تم تصدير ملف Excel بنجاح");
  };

  const exportPdf = async () => {
    const isEndYear = certType === "end_year";
    if (isEndYear ? !detailed : !matrix) return;
    try {
      const container = document.createElement("div");
      container.style.position = "fixed";
      container.style.left = "-99999px";
      container.style.top = "0";
      container.style.direction = "rtl";
      container.style.background = "#ffffff";
      // نهاية العام تحتاج عرضًا أكبر (9 أعمدة/مادة بدل 3-4) لتبقى الخطوط
      // مقروءة قبل أن تُصغَّر لتناسب عرض صفحة A4.
      container.style.width = isEndYear ? "2200px" : "1400px";
      container.style.padding = "24px";
      document.body.appendChild(container);
      const { createRoot } = await import("react-dom/client");
      const root = createRoot(container);
      await new Promise((resolve) => {
        root.render(
          isEndYear
            ? <PrintableEndYearDetailed detailed={detailed} grade={grade} school={school} showGrades={showGrades} />
            : <PrintableMatrix matrix={matrix} grade={grade} certType={certType} school={school} showGrades={showGrades} />
        );
        document.fonts.ready.then(() => setTimeout(resolve, 150));
      });
      const filename = isEndYear
        ? `مراجعة-رصد-${grade}-نهاية-العام-تفصيلي.pdf`
        : `مراجعة-رصد-${grade}-${certType}.pdf`;
      // نهاية العام تُطبَع على A3 أفقي (بدل A4 عمودي) — عدد الأعمدة الكبير
      // (9 لكل مادة) يحتاج مساحة أوسع بكثير حتى يبقى الخط مقروءًا؛ الفصل
      // الأول والثاني (المسار الآخر أدناه) يبقيان A4 كما كانا تمامًا.
      await downloadCertificatePdf(container, filename, isEndYear ? { format: "a3", orientation: "l" } : {});
      container.remove();
      toast.success("تم تصدير ملف PDF بنجاح");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  // "PDF 2" — نفس بيانات "Excel 2" بالضبط (الكشف الكامل)، مطبوعة كجدول.
  // عريض جدًا (عمود مستقل لكل مادة + أعمدة ثابتة) → A3 أفقي دائمًا.
  const exportEndYearFullSheetPdf = async () => {
    if (!detailed) return;
    // تحذير مبكر: لفصل كبير مع أعمدة مواد كثيرة، توليد صورة عالية الدقة
    // لجدول بهذا الحجم قد يأخذ وقتًا ملحوظًا ويجمّد المتصفح مؤقتًا (رسالة
    // "انتظر/إغلاق الصفحة" هي تحذير المتصفح نفسه، وليست خطأ في الملف) —
    // نعلم المستخدم صراحة بدل ترك الأمر بلا تفسير.
    toast.info("جارٍ تجهيز الملف… قد يستغرق وقتًا أطول للفصول الكبيرة، برجاء الانتظار وعدم إغلاق الصفحة");
    try {
      const container = document.createElement("div");
      container.style.position = "fixed";
      container.style.left = "-99999px";
      container.style.top = "0";
      container.style.direction = "rtl";
      container.style.background = "#ffffff";
      container.style.width = "2600px";
      container.style.padding = "24px";
      document.body.appendChild(container);
      const { createRoot } = await import("react-dom/client");
      const root = createRoot(container);
      await new Promise((resolve) => {
        root.render(<PrintableEndYearFullSheet detailed={detailed} grade={grade} school={school} showGrades={showGrades} />);
        document.fonts.ready.then(() => setTimeout(resolve, 150));
      });
      // scale أقل (2 بدل 3 الافتراضي) وهامش أقل (5مم بدل 10) خصيصًا لهذا
      // التقرير العريض جدًا — يقلّل حجم الصورة الناتجة بشكل كبير (يمنع
      // تجميد المتصفح)، ويمنح الأعمدة والنصوص مساحة أوضح على الصفحة. باقي
      // كل تصديرات PDF الأخرى في الموقع غير متأثرة (لا تزال scale:3، هامش
      // 10مم افتراضيًا).
      await downloadCertificatePdf(container, `كشف-كامل-نهاية-العام-${grade}.pdf`, {
        format: fullSheetPdfFormat, orientation: "l", scale: 2, marginMm: 5,
      });
      container.remove();
      toast.success("تم تصدير ملف PDF بنجاح");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="space-y-6" data-testid="reports-page">
      <PageHeader
        icon={FileText}
        overline="التقارير والشهادات"
        title="تقارير الرصد والترتيب"
        subtitle="اطلع على مصفوفة رصد المواد بألوان مميّزة وترتيب الطلاب وتقارير نهاية العام التفصيلية."
      />

      <div className="rounded-xl border border-border bg-white p-4 grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
        <SF label="الصف" value={grade} onChange={setGrade} options={grades} />
        <SF label="نوع التقرير" value={certType} onChange={setCertType}
             options={["semester1", "semester2", "end_year"]}
             labels={{ semester1: "الفصل الأول", semester2: "الفصل الثاني", end_year: "نهاية العام" }} />
        <Button onClick={load} disabled={!grade || loading} className="h-10" data-testid="reports-load-btn">
          {loading ? "جارٍ التحميل…" : "عرض التقرير"}
        </Button>
        {matrix && (
          <div className="flex gap-2">
            <Button variant="outline" onClick={exportExcel} className="h-10 flex-1" data-testid="reports-export-xlsx">
              <FileSpreadsheet className="h-4 w-4 me-1.5" /> Excel
            </Button>
            <Button variant="outline" onClick={exportPdf} className="h-10 flex-1" data-testid="reports-export-pdf">
              <FileDown className="h-4 w-4 me-1.5" /> PDF
            </Button>
          </div>
        )}
        {/* "Excel 2 / PDF 2" — لنهاية العام فقط. تقرير مختلف تمامًا (كشف كامل
            بنمط البرنامج المكتبي: صف/طالب لكل فصل + بيانات إضافية)، منفصل
            بالكامل عن Excel/PDF أعلاه — لا يستبدلهما ولا يؤثر عليهما. */}
        {certType === "end_year" && detailed && (
          <div className="flex gap-2 items-center">
            <Button variant="outline" onClick={exportEndYearFullSheetExcel} className="h-10 flex-1" data-testid="reports-export-xlsx-2">
              <FileSpreadsheet className="h-4 w-4 me-1.5" /> Excel 2
            </Button>
            {/* اختيار مقاس صفحة PDF 2 (A4/A3) — لا يؤثر على أي تصدير آخر. */}
            <Select value={fullSheetPdfFormat} onValueChange={setFullSheetPdfFormat}>
              <SelectTrigger className="h-10 w-20" data-testid="reports-pdf2-format"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="a4">A4</SelectItem>
                <SelectItem value="a3">A3</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={exportEndYearFullSheetPdf} className="h-10 flex-1" data-testid="reports-export-pdf-2">
              <FileDown className="h-4 w-4 me-1.5" /> PDF 2
            </Button>
          </div>
        )}
        {matrix?.use_bands && (
          <label className="flex items-center gap-2 text-sm md:col-span-4 cursor-pointer">
            <input type="checkbox" checked={showGrades} onChange={(e) => setShowGrades(e.target.checked)}
                   className="h-4 w-4 accent-primary" data-testid="reports-show-grades" />
            إظهار الدرجات في التصدير (العمود يُلوَّن دائمًا حسب التقدير سواء أظهرت الرقم أو أخفيته — يظهر فقط لأن هذا الصف من الروضة/الابتدائي)
          </label>
        )}
      </div>

      {(rank || matrix) && (
        <Tabs defaultValue="matrix" className="space-y-4">
          <TabsList className="bg-white border border-border">
            <TabsTrigger value="matrix" data-testid="tab-matrix">
              <LayoutGrid className="h-4 w-4 me-1.5" /> مصفوفة الرصد
            </TabsTrigger>
            <TabsTrigger value="rank" data-testid="tab-rank">
              <Trophy className="h-4 w-4 me-1.5" /> ترتيب الطلاب
            </TabsTrigger>
            {certType === "end_year" && (
              <TabsTrigger value="detailed" data-testid="tab-detailed">
                <CalendarDays className="h-4 w-4 me-1.5" /> نهاية العام التفصيلي
              </TabsTrigger>
            )}
          </TabsList>

          <TabsContent value="matrix">
            {matrix && <MatrixTable matrix={matrix} />}
          </TabsContent>
          <TabsContent value="rank">
            {rank && <RankTable rank={rank} />}
          </TabsContent>
          {certType === "end_year" && (
            <TabsContent value="detailed">
              {detailed && <EndYearDetailed data={detailed} />}
            </TabsContent>
          )}
        </Tabs>
      )}
    </div>
  );
}

const CERT_TYPE_LABELS = { semester1: "الفصل الدراسي الأول", semester2: "الفصل الدراسي الثاني", end_year: "نهاية العام" };
const REVIEW_GRID_COLOR = "#94A3B8";
const REVIEW_DIVIDER_COLOR = "#1F2937";
const REVIEW_HEADER_TEAL = "#0D9488";
const REVIEW_STRIPE = "#F1F5F9";

/** نسخة مسطّحة من مصفوفة الرصد (بلا Sticky أو تمرير) — تُستخدَم فقط
 * عند التقاطها كصورة لتصدير PDF، حتى يظهر الجدول كاملًا بدون قطع.
 * الألوان والتنسيق هنا مطابقان تمامًا لتصدير PDF في البرنامج المكتبي:
 * رأس رقم الجلوس/الاسم بخلفية تركوازية صلبة ونص أبيض، رأس كل مادة
 * بلون المادة الكامل (لا تفتيح) ونص أبيض، خلايا الدرجات بخلفية لونية
 * صلبة (نجاح/رسوب) ونص أبيض، وخط فاصل غامق بين كل مادة والتالية. */
function PrintableMatrix({ matrix, grade, certType, school, showGrades = true }) {
  const showOfficial = matrix.show_official_grade;
  const showElementary = matrix.show_elementary_scale;
  // كل مادة تعرض: أعمال السنة، الاختبار النهائي، المجموع (الخام)، ثم
  // الدرجة الرسمية (للإعدادية فقط) أو التقدير النصّي (للابتدائي فقط) —
  // نفس أعمدة البرنامج المكتبي بالضبط.
  const colsPerSubject = (showOfficial || showElementary) ? 4 : 3;
  const gridStyle = { borderColor: REVIEW_GRID_COLOR };
  return (
    <div>
      <div className="text-center mb-3 border-b-2 pb-2" style={{ borderColor: REVIEW_HEADER_TEAL }}>
        <div className="font-display text-lg font-bold" style={{ color: REVIEW_HEADER_TEAL }}>
          {school?.ministry_name || "وزارة التعليم"}
        </div>
        {school?.education_dept && <div className="text-sm font-medium">{school.education_dept}</div>}
        {school?.office_name && <div className="text-sm font-medium">{school.office_name}</div>}
        <div className="font-display text-base font-bold">{school?.school_name || "المدرسة"}</div>
      </div>
      <h2 className="font-display text-xl font-bold text-center mb-1">مراجعة رصد الدرجات</h2>
      <p className="text-center text-sm text-muted-foreground mb-4">
        الصف: {grade} — {CERT_TYPE_LABELS[certType]}
      </p>
      <table className="w-full text-xs border-collapse" style={{ border: `1.6px solid #000` }}>
        <thead>
          <tr>
            <th rowSpan={2} className="px-2 py-2 text-start font-bold text-white border"
                style={{ ...gridStyle, backgroundColor: REVIEW_HEADER_TEAL }}>الطالب</th>
            <th rowSpan={2} className="px-2 py-2 text-center font-bold text-white border"
                style={{ ...gridStyle, backgroundColor: REVIEW_HEADER_TEAL }}>جلوس</th>
            {matrix.subjects.map((s, i) => (
              <th key={s.name} colSpan={colsPerSubject}
                  className="px-2 py-2 text-center font-bold text-white border"
                  style={{
                    ...gridStyle, backgroundColor: s.color,
                    borderInlineEnd: i < matrix.subjects.length - 1 ? `2.2px solid ${REVIEW_DIVIDER_COLOR}` : gridStyle.borderColor,
                  }}>
                {s.name}
              </th>
            ))}
          </tr>
          <tr>
            {matrix.subjects.map((s, i) => {
              const isLastSubject = i === matrix.subjects.length - 1;
              const subCols = showOfficial
                ? ["أعمال السنة", "الاختبار النهائي", "المجموع", "الدرجة الرسمية"]
                : showElementary
                ? ["أعمال السنة", "الاختبار النهائي", "الدرجة", "التقدير"]
                : ["أعمال السنة", "الاختبار النهائي", "المجموع"];
              return subCols.map((label, k) => (
                <th key={`${s.name}-${k}`} className="px-1 py-1 text-[10px] font-bold text-white border"
                    style={{
                      ...gridStyle, backgroundColor: s.color,
                      borderInlineEnd: (k === subCols.length - 1 && !isLastSubject) ? `2.2px solid ${REVIEW_DIVIDER_COLOR}` : gridStyle.borderColor,
                    }}>
                  {label}
                </th>
              ));
            })}
          </tr>
        </thead>
        <tbody>
          {matrix.students.map((st, ri) => (
            <tr key={st.student_id} style={{ backgroundColor: ri % 2 === 1 ? REVIEW_STRIPE : "#ffffff" }}>
              <td className="px-2 py-1.5 border" style={gridStyle}>{st.name}</td>
              <td className="px-2 py-1.5 text-center border" style={gridStyle}>{st.seat_number || "—"}</td>
              {matrix.subjects.map((s, i) => {
                const cell = st.cells[s.name] || {};
                const isLastSubject = i === matrix.subjects.length - 1;
                // الابتدائي: بلا أي تلوين إطلاقًا (نظام الألوان أُلغي منها) —
                // حتى لو كانت البيانات تحمل لونًا (لأغراض الرسوم البيانية في
                // التحليل الإحصائي)، لا يُستخدَم هنا. الروضة/الإعدادي (شبه
                // نجاح/رسوب): بلا تغيير.
                const cellColorStyle = showElementary ? {} : { backgroundColor: cell.color, color: cell.color ? `#${contrastTextColor(cell.color)}` : undefined };
                const vals = showOfficial
                  ? [cell.year_work, cell.final_exam, cell.total, cell.official_grade]
                  : showElementary
                  ? [cell.year_work, cell.final_exam, cell.total, cell.label]
                  : [cell.year_work, cell.final_exam, cell.total];
                return vals.map((v, k) => {
                  const isLastCol = k === vals.length - 1;
                  const isLabelCol = showElementary && k === 3;
                  const isTotalOrOfficial = showOfficial ? k >= 2 : (showElementary ? k === 2 : k === 2);
                  // إخفاء الرقم فقط عند إيقاف "إظهار الدرجات" — متاح فقط
                  // لمرحلة الألوان (الروضة)؛ matrix.use_bands أصبحت خاصة
                  // بالروضة فقط، فتبقى قيم الابتدائي ظاهرة دائمًا تلقائيًا.
                  const hideValue = matrix.use_bands && isTotalOrOfficial && !showGrades;
                  return (
                    <td key={`${s.name}-${k}`} className={`px-1 py-1.5 text-center border ${(isTotalOrOfficial || isLabelCol) ? "font-bold" : ""}`}
                        style={{
                          ...gridStyle,
                          ...(isTotalOrOfficial ? cellColorStyle : {}),
                          borderInlineEnd: (isLastCol && !isLastSubject) ? `2.2px solid ${REVIEW_DIVIDER_COLOR}` : gridStyle.borderColor,
                        }}>
                      {hideValue ? "" : (v ?? "—")}
                    </td>
                  );
                });
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** نسخة PDF من تبويب "نهاية العام التفصيلي" — 9 أعمدة لكل مادة (نفس بنية
 * exportEndYearDetailedExcel أعلاه ونفس تبويب EndYearDetailed المعروض على
 * الشاشة)، بنفس هوية الألوان والحدود المستخدَمة في PrintableMatrix. */
function PrintableEndYearDetailed({ detailed, grade, school, showGrades = true }) {
  const gridStyle = { borderColor: REVIEW_GRID_COLOR };
  const subCols = ["أ.السنة", "النهائي", "الخام", "الرسمية"];
  const cellKeys = ["yw1", "fe1", "raw1", "official1", "yw2", "fe2", "raw2", "official2", "end_year"];
  return (
    <div>
      <div className="text-center mb-3 border-b-2 pb-2" style={{ borderColor: REVIEW_HEADER_TEAL }}>
        <div className="font-display text-lg font-bold" style={{ color: REVIEW_HEADER_TEAL }}>
          {school?.ministry_name || "وزارة التعليم"}
        </div>
        {school?.education_dept && <div className="text-sm font-medium">{school.education_dept}</div>}
        {school?.office_name && <div className="text-sm font-medium">{school.office_name}</div>}
        <div className="font-display text-base font-bold">{school?.school_name || "المدرسة"}</div>
      </div>
      <h2 className="font-display text-xl font-bold text-center mb-1">مراجعة رصد الدرجات — نهاية العام (تفصيلي)</h2>
      <p className="text-center text-sm text-muted-foreground mb-4">الصف: {grade} — نهاية العام</p>
      <table className="w-full text-[10px] border-collapse" style={{ border: `1.6px solid #000` }}>
        <thead>
          <tr>
            <th rowSpan={3} className="px-2 py-2 text-start font-bold text-white border"
                style={{ ...gridStyle, backgroundColor: REVIEW_HEADER_TEAL }}>الطالب</th>
            <th rowSpan={3} className="px-2 py-2 text-center font-bold text-white border"
                style={{ ...gridStyle, backgroundColor: REVIEW_HEADER_TEAL }}>جلوس</th>
            {detailed.subjects.map((s, i) => (
              <th key={s.name} colSpan={9}
                  className="px-2 py-2 text-center font-bold text-white border"
                  style={{
                    ...gridStyle, backgroundColor: s.color,
                    borderInlineEnd: i < detailed.subjects.length - 1 ? `2.2px solid ${REVIEW_DIVIDER_COLOR}` : gridStyle.borderColor,
                  }}>
                {s.name}
              </th>
            ))}
          </tr>
          <tr>
            {detailed.subjects.map((s, i) => {
              const isLastSubject = i === detailed.subjects.length - 1;
              return (
                <Fragment key={`${s.name}-grp`}>
                  <th colSpan={4} className="px-1 py-1 text-[9px] font-bold text-white border" style={{ ...gridStyle, backgroundColor: s.color }}>الفصل الأول</th>
                  <th colSpan={4} className="px-1 py-1 text-[9px] font-bold text-white border" style={{ ...gridStyle, backgroundColor: s.color }}>الفصل الثاني</th>
                  <th className="px-1 py-1 text-[9px] font-bold text-white border"
                       style={{ ...gridStyle, backgroundColor: s.color, borderInlineEnd: !isLastSubject ? `2.2px solid ${REVIEW_DIVIDER_COLOR}` : gridStyle.borderColor }}>
                    نهاية العام
                  </th>
                </Fragment>
              );
            })}
          </tr>
          <tr>
            {detailed.subjects.map((s, i) => {
              const isLastSubject = i === detailed.subjects.length - 1;
              return (
                <Fragment key={`${s.name}-sub`}>
                  {subCols.map((label, k) => (
                    <th key={`${s.name}-1-${k}`} className="px-1 py-0.5 text-[9px] font-bold text-white border" style={{ ...gridStyle, backgroundColor: s.color }}>{label}</th>
                  ))}
                  {subCols.map((label, k) => (
                    <th key={`${s.name}-2-${k}`} className="px-1 py-0.5 text-[9px] font-bold text-white border" style={{ ...gridStyle, backgroundColor: s.color }}>{label}</th>
                  ))}
                  <th className="px-1 py-0.5 text-[9px] font-bold text-white border"
                       style={{ ...gridStyle, backgroundColor: s.color, borderInlineEnd: !isLastSubject ? `2.2px solid ${REVIEW_DIVIDER_COLOR}` : gridStyle.borderColor }}>
                    الدرجة
                  </th>
                </Fragment>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {detailed.students.map((st, ri) => (
            <tr key={st.student_id} style={{ backgroundColor: ri % 2 === 1 ? REVIEW_STRIPE : "#ffffff" }}>
              <td className="px-2 py-1.5 border" style={gridStyle}>{st.name}</td>
              <td className="px-2 py-1.5 text-center border" style={gridStyle}>{st.seat_number || "—"}</td>
              {detailed.subjects.map((s, i) => {
                const cell = st.subjects[s.name] || {};
                const isLastSubject = i === detailed.subjects.length - 1;
                if (cell.type === "outcome") {
                  return (
                    <td key={s.name} colSpan={9} className="px-2 py-1.5 text-center border font-medium"
                         style={{ ...gridStyle, borderInlineEnd: !isLastSubject ? `2.2px solid ${REVIEW_DIVIDER_COLOR}` : gridStyle.borderColor }}>
                      {cell.outcome_text || "—"}
                    </td>
                  );
                }
                const vals = [cell.yw1, cell.fe1, cell.raw1, cell.official1, cell.yw2, cell.fe2, cell.raw2, cell.official2, cell.end_year];
                return vals.map((v, k) => {
                  const isLastCol = k === vals.length - 1;
                  // نظام الألوان الفعلي (يفوق التوقعات/...) — فقط لمراحل
                  // الروضة/الابتدائي، ولأعمدة الخام/الرسمية/نهاية العام.
                  const colorKey = `${cellKeys[k]}_color`;
                  const bandColor = detailed.use_bands ? cell[colorKey] : null;
                  const hideValue = detailed.use_bands && bandColor && !showGrades;
                  return (
                    <td key={`${s.name}-${k}`} className={`px-1 py-1.5 text-center border ${bandColor ? "font-bold" : ""}`}
                        style={{
                          ...gridStyle,
                          ...(bandColor ? { backgroundColor: bandColor, color: `#${contrastTextColor(bandColor)}` } : {}),
                          borderInlineEnd: (isLastCol && !isLastSubject) ? `2.2px solid ${REVIEW_DIVIDER_COLOR}` : gridStyle.borderColor,
                        }}>
                      {hideValue ? "" : (v == null ? "—" : Number(v).toFixed(1))}
                    </td>
                  );
                });
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** نسخة PDF من "الكشف الكامل" (نفس بيانات Excel 2 بالضبط): 3 صفوف لكل
 * طالب (الفصل الأول/الفصل الثاني/الدور الأول)، عمود مستقل لكل مادة، مع
 * رقم الجلوس/الاسم/القومي/السفارة/المدرسة/نظام الدراسة/اللغات/الملاحظات. */
function PrintableEndYearFullSheet({ detailed, grade, school, showGrades = true }) {
  const gridStyle = { borderColor: REVIEW_GRID_COLOR };
  const TERM_LABELS = ["الفصل الأول", "الفصل الثاني", "الدور الأول"];
  const th = (label, extra = "") => (
    <th key={label} className={`px-1 py-2 text-center font-bold text-white border ${extra}`} style={{ ...gridStyle, backgroundColor: REVIEW_HEADER_TEAL }}>
      {label}
    </th>
  );
  return (
    <div>
      <div className="text-center mb-3 border-b-2 pb-2" style={{ borderColor: REVIEW_HEADER_TEAL }}>
        <div className="font-display text-lg font-bold" style={{ color: REVIEW_HEADER_TEAL }}>
          {school?.ministry_name || "وزارة التعليم"}
        </div>
        {school?.education_dept && <div className="text-sm font-medium">{school.education_dept}</div>}
        {school?.office_name && <div className="text-sm font-medium">{school.office_name}</div>}
        <div className="font-display text-base font-bold">{school?.school_name || "المدرسة"}</div>
      </div>
      <h2 className="font-display text-xl font-bold text-center mb-1">كشف رصد درجات — نهاية العام (كامل)</h2>
      <p className="text-center text-sm text-muted-foreground mb-4">الصف: {grade} — نهاية العام</p>
      {/*
        ملاحظة تصميم مهمة: تعمّدنا عدم استخدام rowSpan/colSpan في جدول الـ
        PDF هذا — أداة html2canvas (المستخدَمة لتحويل الجدول لصورة قبل
        وضعه في ملف PDF) لها قصور معروف وموثَّق في حساب حدود الخلايا
        المدموجة (rowSpan) بدقة، وأحيانًا تُقصّ محتوياتها أو لا تظهر
        إطلاقًا — وهذا هو السبب الحقيقي لعدم ظهور الاسم والرقم القومي في
        النسخة السابقة. البديل الآمن: كل صف مستقل بخلاياه الكاملة (بلا أي
        دمج)، مع ترك خلايا رقم الجلوس/الاسم/السفارة فارغة في الصفين
        الإضافيين لكل طالب بدل دمجها — يضمن ظهور كل المحتوى دائمًا.
      */}
      <table className="w-full text-[9px] border-collapse" style={{ border: "1.6px solid #000" }}>
        <thead>
          <tr>
            {th("رقم الجلوس", "text-start")}
            {th("الاسم والرقم القومي", "text-start")}
            {th("السفارة والمدرسة")}
            {th("الفصل")}
            {detailed.subjects.map((s) => (
              <th key={s.name} className="px-1 py-2 text-center font-bold text-white border" style={{ ...gridStyle, backgroundColor: s.color }}>
                {s.name}<br /><span className="text-[8px] font-normal">({s.max_score}/{s.min_score})</span>
              </th>
            ))}
            {th("المجموع الكلي")}
            {th("نظام الدراسة")}
            {th("لغة أجنبية 1")}
            {th("لغة أجنبية 2")}
            {th("ملاحظات")}
          </tr>
        </thead>
        <tbody>
          {detailed.students.map((st, si) => (
            <Fragment key={st.student_id}>
              {[0, 1, 2].map((t) => (
                <tr key={t} style={{ backgroundColor: si % 2 === 1 ? REVIEW_STRIPE : "#ffffff" }}>
                  <td className="px-2 py-1.5 border align-top" style={gridStyle}>
                    {t === 0 && <span className="font-semibold">{st.seat_number || "—"}</span>}
                  </td>
                  <td className="px-2 py-1.5 border align-top" style={gridStyle}>
                    {t === 0 && (
                      <>
                        <div>{st.name}</div>
                        <div className="text-[8px]" style={{ color: "#475569" }}>قومي: {st.national_id || "—"}</div>
                      </>
                    )}
                  </td>
                  <td className="px-2 py-1.5 text-center border align-top" style={gridStyle}>
                    {t === 0 && (
                      <>
                        <div>{school?.embassy_name || "—"}</div>
                        <div className="text-[8px]" style={{ color: "#475569" }}>{school?.school_name || "—"}</div>
                      </>
                    )}
                  </td>
                  <td className="px-1 py-1.5 text-center border font-medium" style={gridStyle}>{TERM_LABELS[t]}</td>
                  {detailed.subjects.map((s) => {
                    const cell = st.subjects[s.name] || {};
                    let display;
                    let cellStyle = gridStyle;
                    if (cell.type === "outcome") display = t === 2 ? (cell.outcome_text || "—") : "";
                    else {
                      const v = t === 0 ? cell.official1 : t === 1 ? cell.official2 : cell.end_year;
                      const colorKey = t === 0 ? "official1_color" : t === 1 ? "official2_color" : "end_year_color";
                      const bandColor = detailed.use_bands ? cell[colorKey] : null;
                      if (bandColor) cellStyle = { ...gridStyle, backgroundColor: bandColor, color: `#${contrastTextColor(bandColor)}` };
                      const hideValue = detailed.use_bands && bandColor && !showGrades;
                      display = hideValue ? "" : (v == null ? "—" : Number(v).toFixed(1));
                    }
                    return <td key={s.name} className="px-1 py-1.5 text-center border font-bold" style={cellStyle}>{display}</td>;
                  })}
                  <td className="px-1 py-1.5 text-center border font-bold" style={gridStyle}>
                    {t === 0 ? st.totals.sem1 : t === 1 ? st.totals.sem2 : st.totals.end_year}
                  </td>
                  <td className="px-1 py-1.5 text-center border align-top" style={gridStyle}>{t === 0 ? (st.section || "—") : ""}</td>
                  <td className="px-1 py-1.5 text-center border align-top" style={gridStyle}>{t === 0 ? (st.foreign_lang1 || "—") : ""}</td>
                  <td className="px-1 py-1.5 text-center border align-top" style={gridStyle}>{t === 0 ? (st.foreign_lang2 || "—") : ""}</td>
                  <td className="px-2 py-1.5 border align-top" style={gridStyle}>{t === 0 ? (st.notes.join(" / ") || "") : ""}</td>
                </tr>
              ))}
            </Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
}


function MatrixTable({ matrix }) {
  const showOfficial = matrix.show_official_grade;
  const showElementary = matrix.show_elementary_scale;
  const subCols = (showOfficial || showElementary) ? 4 : 3;
  return (
    <div className="rounded-xl border border-border bg-white overflow-hidden">
      <div className="px-5 py-4 border-b border-border">
        <h3 className="font-display text-lg font-semibold">مصفوفة رصد المواد</h3>
        <div className="text-[12px] text-muted-foreground mt-0.5">
          كل مادة بلون مميّز — أعمال السنة، الاختبار النهائي، المجموع{showOfficial ? "، والدرجة الرسمية بعد التحويل" : showElementary ? "، والتقدير" : ""}.
        </div>
      </div>
      <div className="overflow-x-auto max-h-[75vh]">
        <table className="w-full text-xs border-collapse">
          <thead className="bg-slate-50/90 border-b border-border sticky top-0 z-10">
            <tr>
              <th rowSpan={2}
                   className="px-2 py-2 text-start font-semibold sticky start-0 bg-slate-50/95 z-20 min-w-[12rem] border-e-2 border-slate-300">
                الطالب
              </th>
              <th rowSpan={2}
                   className="px-2 py-2 text-end font-semibold border-e-2 border-slate-300">جلوس</th>
              {matrix.subjects.map((s, i) => (
                <th
                  key={s.name}
                  colSpan={subCols}
                  className="px-2 py-2 text-center font-semibold whitespace-nowrap border-s-2 border-e-2 border-slate-200"
                  style={{ backgroundColor: lighten(s.color, 0.18), color: s.color }}
                >
                  {s.name}
                </th>
              ))}
            </tr>
            <tr>
              {matrix.subjects.map((s) => {
                const labels = showOfficial
                  ? ["أعمال السنة", "الاختبار النهائي", "المجموع", "الرسمية"]
                  : showElementary
                  ? ["أعمال السنة", "الاختبار النهائي", "الدرجة", "التقدير"]
                  : ["أعمال السنة", "الاختبار النهائي", "المجموع"];
                return (
                  <Fragment key={s.name}>
                    {labels.map((lbl, k) => (
                      <th key={k}
                          className={"px-2 py-1 text-[10px] font-semibold text-center whitespace-nowrap border-slate-200 " + (k === 0 ? "border-s-2 " : "") + (k === labels.length - 1 ? "border-e-2" : "")}
                          style={{ backgroundColor: lighten(s.color, 0.08), color: s.color }}>{lbl}</th>
                    ))}
                  </Fragment>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {matrix.students.map((st) => (
              <tr key={st.student_id} className="border-b border-slate-100 last:border-0">
                <td className="px-2 py-2 font-medium sticky start-0 bg-white border-e-2 border-slate-200">{st.name}</td>
                <td className="px-2 py-2 text-end tabular-nums text-muted-foreground border-e-2 border-slate-200">{st.seat_number || "—"}</td>
                {matrix.subjects.map((s) => {
                  const cell = st.cells[s.name];
                  const bg = lighten(s.color, 0.06);
                  const vals = showOfficial
                    ? [cell.year_work, cell.final_exam, cell.total, cell.official_grade]
                    : showElementary
                    ? [cell.year_work, cell.final_exam, cell.total, cell.label]
                    : [cell.year_work, cell.final_exam, cell.total];
                  return (
                    <Fragment key={s.name}>
                      {vals.map((v, k) => {
                        const isLabelCol = showElementary && k === 3;
                        const isEmphasized = showOfficial ? k >= 2 : k === 2 || isLabelCol;
                        const isLastColOfSubject = k === vals.length - 1;
                        // الابتدائي: بلا أي تلوين لخلية "الدرجة" (نظام
                        // الألوان أُلغي منها) — تظهر بخط عادي مثل باقي
                        // الأعمدة، وعمود "التقدير" الجديد نص فقط (بلا لون
                        // خلفية أو نص ملوَّن).
                        const applyColor = !showElementary;
                        return (
                          <td key={k}
                              className={"px-2 py-1.5 text-center border-slate-200 " + (k === 0 ? "border-s-2 " : "") + (isLastColOfSubject ? "border-e-2" : "")}
                              style={{ backgroundColor: bg }}>
                            {isEmphasized ? (
                              <div className={"rounded-md px-1.5 py-1 tabular-nums inline-block " + (isLastColOfSubject && showOfficial ? "font-semibold" : "font-medium")}
                                    style={(isLastColOfSubject && showOfficial && applyColor)
                                      ? { backgroundColor: cell.color + "22", color: cell.color }
                                      : (applyColor ? { color: cell.color } : { color: isLabelCol ? "#334155" : undefined })}>
                                {v ?? "—"}
                              </div>
                            ) : (
                              <span className="tabular-nums text-muted-foreground">{v ?? "—"}</span>
                            )}
                          </td>
                        );
                      })}
                    </Fragment>
                  );
                })}
              </tr>
            ))}
            {matrix.students.length === 0 && (
              <tr>
                <td colSpan={2 + matrix.subjects.length * subCols} className="px-4 py-10 text-center text-muted-foreground">
                  لا يوجد طلاب لهذا التحديد
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
function RankTable({ rank }) {
  return (
    <div className="rounded-xl border border-border bg-white overflow-hidden">
      <div className="px-5 py-4 border-b border-border flex items-center justify-between">
        <h3 className="font-display text-lg font-semibold flex items-center gap-2">
          <Trophy className="h-5 w-5 text-amber-600" /> ترتيب الطلاب
        </h3>
        <div className="text-[12px] text-muted-foreground">إجمالي: {rank.length} طالب</div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50/70 border-b border-border">
            <tr>
              <th className="px-3 py-2.5 text-center text-[11px] font-semibold text-muted-foreground uppercase w-16">ترتيب</th>
              <th className="px-3 py-2.5 text-start text-[11px] font-semibold text-muted-foreground uppercase">اسم الطالب</th>
              <th className="px-3 py-2.5 text-start text-[11px] font-semibold text-muted-foreground uppercase">الفصل</th>
              <th className="px-3 py-2.5 text-end text-[11px] font-semibold text-muted-foreground uppercase">جلوس</th>
              <th className="px-3 py-2.5 text-end text-[11px] font-semibold text-muted-foreground uppercase">المجموع</th>
              <th className="px-3 py-2.5 text-end text-[11px] font-semibold text-muted-foreground uppercase">النسبة</th>
              <th className="px-3 py-2.5 text-center text-[11px] font-semibold text-muted-foreground uppercase">الحالة</th>
            </tr>
          </thead>
          <tbody>
            {rank.map((r) => (
              <tr key={r.student_id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50/60">
                <td className="px-3 py-2.5 text-center">
                  <span className={"inline-flex h-7 w-7 items-center justify-center rounded-full text-xs font-bold " +
                    (r.rank === 1 ? "bg-amber-100 text-amber-700" : r.rank === 2 ? "bg-slate-200 text-slate-700" : r.rank === 3 ? "bg-orange-100 text-orange-700" : "bg-slate-50 text-slate-500")}>
                    {r.rank}
                  </span>
                </td>
                <td className="px-3 py-2.5 font-medium">{r.name}</td>
                <td className="px-3 py-2.5">{r.class || "—"}</td>
                <td className="px-3 py-2.5 text-end tabular-nums text-muted-foreground text-xs">{r.seat_number || "—"}</td>
                <td className="px-3 py-2.5 text-end tabular-nums font-semibold">
                  {r.total.toFixed(1)}<span className="text-muted-foreground text-xs"> / {r.max_total}</span>
                </td>
                <td className="px-3 py-2.5 text-end tabular-nums">{r.percentage}%</td>
                <td className="px-3 py-2.5 text-center">
                  <span className={"inline-flex px-2 py-0.5 rounded-md text-[11px] font-medium " +
                    (r.passed ? "bg-emerald-50 text-emerald-700" : "bg-rose-50 text-rose-700")}>
                    {r.passed ? "ناجح" : "متعثر"}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function EndYearDetailed({ data }) {
  const cols = ["yw1", "fe1", "raw1", "official1", "yw2", "fe2", "raw2", "official2", "end_year"];
  const colLabels = {
    yw1: "أ.السنة", fe1: "النهائي", raw1: "الخام", official1: "الرسمية",
    yw2: "أ.السنة", fe2: "النهائي", raw2: "الخام", official2: "الرسمية",
    end_year: "نهاية العام",
  };
  const groupLabels = {
    sem1: "الفصل الأول",
    sem2: "الفصل الثاني",
  };
  const fmt = (v) => (v == null ? "—" : Number(v).toFixed(1));
  return (
    <div className="rounded-xl border border-border bg-white overflow-hidden">
      <div className="px-5 py-4 border-b border-border">
        <h3 className="font-display text-lg font-semibold flex items-center gap-2">
          <CalendarDays className="h-5 w-5 text-primary" /> نهاية العام — تفصيلي
        </h3>
        <div className="text-[12px] text-muted-foreground mt-0.5">
          9 أعمدة لكل مادة: (أعمال السنة، الاختبار، الخام، الرسمية) × فصلين + الدرجة النهائية لنهاية العام.
        </div>
      </div>
      <div className="overflow-x-auto max-h-[75vh]">
        <table className="w-full text-[11px] border-collapse">
          <thead className="bg-slate-50 border-b border-slate-200 sticky top-0 z-10">
            <tr>
              <th rowSpan={3} className="px-2 py-1 text-start font-semibold sticky start-0 bg-slate-50 z-20 border-e-2 border-slate-300 min-w-[10rem]">الطالب</th>
              <th rowSpan={3} className="px-2 py-1 text-end font-semibold border-e-2 border-slate-300">جلوس</th>
              {data.subjects.map((s) => (
                <th key={s.name} colSpan={9}
                     className="px-2 py-1 text-center font-semibold border-s-2 border-e-2 border-slate-300 whitespace-nowrap"
                     style={{ backgroundColor: lighten(s.color, 0.2), color: s.color }}>
                  {s.name}
                </th>
              ))}
            </tr>
            <tr>
              {data.subjects.map((s) => (
                <Fragment key={s.name}>
                  <th colSpan={4} className="px-1 py-0.5 text-center border-s-2 border-slate-200 text-[10px] whitespace-nowrap"
                       style={{ backgroundColor: lighten(s.color, 0.1), color: s.color }}>{groupLabels.sem1}</th>
                  <th colSpan={4} className="px-1 py-0.5 text-center text-[10px] whitespace-nowrap"
                       style={{ backgroundColor: lighten(s.color, 0.1), color: s.color }}>{groupLabels.sem2}</th>
                  <th className="px-1 py-0.5 text-center text-[10px] whitespace-nowrap border-e-2 border-slate-200"
                       style={{ backgroundColor: lighten(s.color, 0.18), color: s.color }}>نهاية العام</th>
                </Fragment>
              ))}
            </tr>
            <tr>
              {data.subjects.map((s) => (
                <Fragment key={s.name}>
                  {cols.slice(0, 4).map((k) => (
                    <th key={k} className={"px-1 py-0.5 text-center text-[10px] font-medium " + (k === "yw1" ? "border-s-2 border-slate-200" : "")}
                         style={{ backgroundColor: lighten(s.color, 0.06), color: s.color }}>{colLabels[k]}</th>
                  ))}
                  {cols.slice(4, 8).map((k) => (
                    <th key={k} className="px-1 py-0.5 text-center text-[10px] font-medium"
                         style={{ backgroundColor: lighten(s.color, 0.06), color: s.color }}>{colLabels[k]}</th>
                  ))}
                  <th className="px-1 py-0.5 text-center text-[10px] font-bold border-e-2 border-slate-200"
                       style={{ backgroundColor: lighten(s.color, 0.14), color: s.color }}>{colLabels.end_year}</th>
                </Fragment>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.students.map((st) => (
              <tr key={st.student_id} className="border-b border-slate-100">
                <td className="px-2 py-1 font-medium sticky start-0 bg-white border-e-2 border-slate-200 whitespace-nowrap">{st.name}</td>
                <td className="px-2 py-1 text-end tabular-nums text-muted-foreground border-e-2 border-slate-200">{st.seat_number || "—"}</td>
                {data.subjects.map((s) => {
                  const cell = st.subjects[s.name] || {};
                  if (cell.type === "outcome") {
                    return (
                      <td key={s.name} colSpan={9} className="px-2 py-1 text-center border-s-2 border-e-2 border-slate-200"
                           style={{ backgroundColor: lighten(s.color, 0.04) }}>
                        <span className="text-[10px] font-medium" style={{ color: s.color }}>{cell.outcome_text}</span>
                      </td>
                    );
                  }
                  return (
                    <Fragment key={s.name}>
                      {cols.map((k, idx) => (
                        <td key={k} className={
                          "px-1 py-1 text-center tabular-nums " +
                          (idx === 0 ? "border-s-2 border-slate-200 " : "") +
                          (idx === cols.length - 1 ? "border-e-2 border-slate-200 font-bold " : "")
                        }
                             style={{ backgroundColor: lighten(s.color, idx === cols.length - 1 ? 0.14 : 0.04),
                                        color: idx === cols.length - 1 ? s.color : undefined }}>
                          {fmt(cell[k])}
                        </td>
                      ))}
                    </Fragment>
                  );
                })}
              </tr>
            ))}
            {data.students.length === 0 && (
              <tr><td colSpan={99} className="text-center py-8 text-muted-foreground">لا يوجد طلاب.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function SF({ label, value, onChange, options, labels }) {
  const opts = options.map((o) => ({ value: o, label: labels?.[o] ?? o }));
  return (
    <div className="space-y-1.5">
      <Label className="text-[12px]">{label}</Label>
      <Select value={value || ""} onValueChange={onChange}>
        <SelectTrigger className="h-10 bg-white"><SelectValue placeholder="اختر" /></SelectTrigger>
        <SelectContent>
          {opts.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}
